import { CommonModule } from '@angular/common';
import {
  Component,
  EventEmitter,
  Input,
  Output,
  inject,
  type OnChanges,
  type OnDestroy,
} from '@angular/core';
import { FormBuilder, ReactiveFormsModule, type FormGroup } from '@angular/forms';
import { TranslocoPipe } from '@jsverse/transloco';
import { LetDirective } from '@ngrx/component';
import { PorscheDesignSystemModule, ToastManager } from '@porsche-design-system/components-angular';
import { BehaviorSubject, distinctUntilChanged, tap } from 'rxjs';
import { SubSink } from 'subsink';
import type {
  EditEduExpensePurpose,
  EduExpensePurpose,
} from '../../../shared/models/api/edu-expense-purpose.model';
import { BudgetType } from '../../../shared/models/api/edu-expense.model';
import type { ControlsOf } from '../../../shared/models/controls-of.model';
import type { NgChanges } from '../../../shared/models/ng-changes.model';
import { EduExpensePurposeService } from '../../../shared/services/api/edu-expense-purpose.service';
import { FormIdService } from '../../../shared/services/form-id.service';
import { OpaValidators } from '../../../shared/validators/opa.validators';
import { budgetTypeTranslation } from '../../edu-expense-create/edu-expense-create.helpers';
import type { EditEduExpensePurposeFormValue } from './edu-expense-purpose-edit.models';

@Component({
  selector: 'opa-edu-expense-purpose-edit',
  imports: [
    CommonModule,
    PorscheDesignSystemModule,
    ReactiveFormsModule,
    LetDirective,
    TranslocoPipe,
  ],
  templateUrl: './edu-expense-purpose-edit.component.html',
  styleUrl: './edu-expense-purpose-edit.component.scss',
})
export class EduExpensePurposeEditComponent implements OnChanges, OnDestroy {
  private readonly formBuilder = inject(FormBuilder).nonNullable;
  private readonly eduExpensePurposeService = inject(EduExpensePurposeService);
  private readonly toastManager = inject(ToastManager);
  readonly formId = inject(FormIdService).getId();

  @Input({ required: true }) open = false;
  @Input({ required: true }) eduExpensePurpose: EduExpensePurpose | undefined = undefined;
  @Output() dismiss = new EventEmitter<boolean | undefined>();

  private readonly subSink = new SubSink();
  readonly form = this.buildForm();
  private readonly submitting = new BehaviorSubject(false);
  readonly submitting$ = this.submitting.pipe(distinctUntilChanged());
  readonly budgetTypes = Object.values(BudgetType);
  readonly budgetTypeTranslation = budgetTypeTranslation;

  ngOnChanges(changes: NgChanges<EduExpensePurposeEditComponent>): void {
    if (changes.open && this.open) this.resetForm(this.eduExpensePurpose);
  }

  ngOnDestroy(): void {
    this.subSink.unsubscribe();
  }

  close(success?: boolean): void {
    this.dismiss.emit(success);
  }

  submit(): void {
    if (!this.form.valid || !this.eduExpensePurpose) {
      this.form.markAllAsTouched();
      return;
    }

    this.submitting.next(true);
    const formValue = this.form.getRawValue();
    const purpose = this.formValueToPurpose(formValue);
    this.subSink.sink = this.eduExpensePurposeService
      .editEduExpensePurpose(this.eduExpensePurpose.id, purpose)
      .pipe(
        tap({
          next: (editedPurpose) => {
            this.toastManager.addMessage({
              text: `Purpose '${editedPurpose.description}' edited`,
              state: 'success',
            });
            this.submitting.next(false);
            this.close(true);
          },
          error: () => this.submitting.next(false),
        }),
      )
      .subscribe();
  }

  private buildForm(): FormGroup<ControlsOf<EditEduExpensePurposeFormValue>> {
    const form = this.formBuilder.group<ControlsOf<EditEduExpensePurposeFormValue>>({
      description: this.formBuilder.control(null, [OpaValidators.required()]),
      budgetType: this.formBuilder.control(null, OpaValidators.required()),
    });

    return form;
  }

  private formValueToPurpose(form: EditEduExpensePurposeFormValue): EditEduExpensePurpose {
    const eduExpensePurpose: EditEduExpensePurpose = {
      name: this.eduExpensePurpose!.name,
      description: form.description!,
      budgetType: form.budgetType!,
    };
    return eduExpensePurpose;
  }

  private resetForm(eduExpensePurpose?: EduExpensePurpose): void {
    this.form.reset();
    if (!eduExpensePurpose) return;

    this.form.patchValue({
      description: eduExpensePurpose.description,
      budgetType: eduExpensePurpose.budgetType,
    });
  }
}
